<template>
  <div v-if="message && message.referal" class="referral-message">
    <div class="referral-card" @click="handleCardClick">
      <!-- Minimalist header with subtle branding -->
      <div class="referral-header">
        <div class="brand-indicator">
          <div class="indicator-dot"></div>
          <span class="brand-text">Anuncio</span>
        </div>
      </div>

      <!-- Compact image section -->
      <div v-if="message.referal.image_url" class="referral-image-container">
        <div class="image-wrapper">
          <img
            :src="message.referal.image_url"
            :alt="message.referal.headline || 'Referral image'"
            class="referral-image"
            @error="onImageError"
            @load="onImageLoad"
          />
          <div class="image-overlay">
            <div class="overlay-icon">
              <i class="fas fa-external-link-alt"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Clean content section -->
      <div v-if="message.referal.body" class="referral-body">
        <div class="content-text">
          <p v-html="$parseMarkdown(message.referal.body)"></p>
        </div>
      </div>

      <!-- Highlighted user message -->
      <div v-if="message.text && message.text.trim()" class="user-message-highlight">
        <div class="user-message-content">
          <div class="message-text">
            <span class="message-label">Respuesta a anuncio:</span>
            <p class="user-text">{{ message.text }}</p>
          </div>
        </div>
      </div>

      <!-- Clean action footer -->
      <div class="referral-footer">
        <div class="footer-content">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    message: {
      type: Object,
      default: () => ({}),
    },
    is_reply: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    onImageError(event) {
      // Hide the image container if the image fails to load
      event.target.closest('.referral-image-container').style.display = 'none';
    },
    onImageLoad(event) {
      // Add loaded class for animation
      event.target.classList.add('loaded');
    },
    handleCardClick() {
      if (this.message.referal.source_url) {
        window.open(this.message.referal.source_url, '_blank');
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.referral-message {
  width: 100%;
  margin: 12px 0;

  .referral-card {
    position: relative;
    background: #ffffff;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.2s ease;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    &:hover {
      border-color: #e2e8f0;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      transform: translateY(-1px);
    }

    // Minimalist header
    .referral-header {
      padding: 16px 20px 12px 20px;
      border-bottom: 1px solid #f8fafc;

      .brand-indicator {
        display: flex;
        align-items: center;
        gap: 8px;

        .indicator-dot {
          width: 8px;
          height: 8px;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          border-radius: 50%;
        }

        .brand-text {
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }
    }

    // Smaller, cleaner image
    .referral-image-container {
      position: relative;
      background: #f8fafc;

      .image-wrapper {
        position: relative;
        overflow: hidden;

        .referral-image {
          width: 100%;
          height: 180px;
          object-fit: cover;
          display: block;
          opacity: 0;
          transition: opacity 0.3s ease;

          &.loaded {
            opacity: 1;
          }
        }

        .image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.4);
          opacity: 0;
          transition: opacity 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;

          .overlay-icon {
            i {
              color: white;
              font-size: 20px;
              transform: scale(0.9);
              transition: transform 0.2s ease;
            }
          }
        }

        &:hover .image-overlay {
          opacity: 1;

          .overlay-icon i {
            transform: scale(1);
          }
        }
      }
    }

    // Clean content section
    .referral-body {
      padding: 20px;

      .content-text {
        p {
          margin: 0;
          color: #374151;
          font-size: 15px;
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-word;

          a {
            color: #3b82f6;
            text-decoration: none;
            font-weight: 500;

            &:hover {
              text-decoration: underline;
            }
          }
        }
      }
    }

    // Highlighted user message section
    .user-message-highlight {
      background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
      border-top: 1px solid #e0f2fe;
      padding: 16px 20px;

      .user-message-content {
        display: flex;
        align-items: flex-start;
        gap: 12px;

        .message-text {
          flex: 1;

          .message-label {
            display: block;
            font-size: 11px;
            font-weight: 600;
            color: #3b82f6;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 4px;
          }

          .user-text {
            margin: 0;
            color: #1e293b;
            font-size: 14px;
            font-weight: 500;
            line-height: 1.4;
          }
        }
      }
    }

    // Clean footer
    .referral-footer {
      padding: 16px 20px;
      background: #fafbfc;
      border-top: 1px solid #f1f5f9;
    }
  }
}

// Mobile responsiveness
@media (max-width: 768px) {
  .referral-message {
    margin: 8px 0;

    .referral-card {
      .referral-header {
        padding: 12px 16px 8px 16px;
      }

      .referral-image-container .image-wrapper .referral-image {
        height: 140px;
      }

      .referral-body {
        padding: 16px;

        .content-text p {
          font-size: 14px;
        }
      }

      .user-message-highlight {
        padding: 12px 16px;

        .user-message-content {
          gap: 10px;

          .message-text .user-text {
            font-size: 13px;
          }
        }
      }

      .referral-footer {
        padding: 12px 16px;
      }
    }
  }
}

// Dark mode support
@media (prefers-color-scheme: dark) {
  .referral-message .referral-card {
    background: #1f2937;
    border-color: #374151;

    .referral-header {
      border-bottom-color: #374151;
    }

    .referral-body .content-text p {
      color: #e5e7eb;
    }

    .user-message-highlight {
      background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%);
      border-top-color: #1e40af;

      .user-message-content .message-text {
        .message-label {
          color: #93c5fd;
        }

        .user-text {
          color: #f1f5f9;
        }
      }
    }

    .referral-footer {
      background: #111827;
      border-top-color: #374151;

      .footer-content .timestamp-section .timestamp {
        color: #9ca3af;
      }
    }
  }
}
</style>
